import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  CheckCircle2,
  CreditCard,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  Wallet,
} from 'lucide-react';
import {
  useAutoRecharge,
  useInvoices,
  usePaymentMethod,
  useRemovePaymentMethod,
  useSetDefaultPaymentMethod,
  useSetupPaymentMethod,
  useUpdateAutoRecharge,
} from '@/hooks/useProducts';
import { useCreditsQuery } from '@/hooks/useAgentWorkspace';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/lib/pricing';
import type { Invoice, SavedCard } from '@/types';

/**
 * Facturation — le panneau de facturation de `SettingsModal`.
 *
 * Quatre blocs, dans l'ordre où l'on s'en sert : **Crédits**, **Moyens de
 * paiement**, **Rechargement automatique**, **Factures**. Chaque valeur est
 * réelle (aucune carte, aucune facture inventées) et chaque action atteint
 * Stripe.
 *
 * ── CE QUE LA PASSE A CHANGÉ, ET POURQUOI ───────────────────────────────────
 *
 * LE PLAN A DISPARU. Le client : « enlève le plan, on a supprimé pour l'instant
 * la notion de plan ». Le bloc lisait `tier`/`plan` de `GET /billing/credits` et
 * ouvrait le portail Stripe pour « modifier l'abonnement » — deux gestes qui ne
 * correspondent plus à rien de vendable. Ce qui reste vrai est le solde et ce
 * qui le recharge.
 *
 * « ACHETER DES CRÉDITS » OUVRE LA POP-UP. Il lançait un Checkout Stripe en
 * dur : `DEFAULT_CREDIT_PACK = 500`, soit 39,99 $ sur le grand-livre du store,
 * sans que l'opérateur choisisse ni ne voie le montant avant la page de
 * paiement. Le client : « pour rajouter des crédits, ouvre la pop-up au lieu de
 * lancer Stripe avec 39,99 € ». C'est aussi le seul tunnel d'achat depuis la
 * suppression de `/pricing`, et il vend les packs AGENT — le solde que l'agent
 * dépense réellement.
 *
 * LES CARTES SE GÈRENT ICI. « Mettre à jour » renvoyait au portail Stripe, qui
 * sort de l'application et revient sur une autre page. Le panneau liste
 * maintenant les cartes, dit laquelle serait débitée, et permet d'en ajouter
 * (capture hébergée par Stripe), d'en promouvoir une, d'en retirer une. Seule la
 * saisie du numéro reste chez Stripe — c'est ce qui garde le 3-D Secure et la
 * conformité de leur côté.
 *
 * LE RECHARGEMENT AUTOMATIQUE EST RÉEL. C'était une pastille « bientôt
 * disponible » sur un bouton désactivé. Le contrat serveur existe désormais
 * pour de bon (`GET`/`PUT /billing/auto-recharge`), avec seuil, montant LIBRE et
 * plafond mensuel.
 *
 * ── CE QUE LE PANNEAU REFUSE DE FAIRE ───────────────────────────────────────
 *
 * Il ne propose jamais d'armer une recharge sans carte enregistrée, ni quand
 * Stripe n'est pas configuré dans l'environnement : le réglage serait accepté et
 * ne partirait jamais. Les deux cas ont leur propre phrase, parce que ce ne sont
 * pas les mêmes — l'un se règle en ajoutant une carte, l'autre pas du tout.
 */

/** Filet quand le serveur répond une forme antérieure — voir `limits` plus bas. */
const DEFAULT_LIMITS = {
  minCredits: 100,
  maxCredits: 10_000,
  maxThreshold: 100_000,
  maxMonthlyCapCents: 1_000_000,
  creditsPerEur: 10,
};

function titleCase(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

/** Square, DA-faithful switch — the same one the Mémoire and Confidentialité panels use. */
function BillingToggle({
  checked,
  onChange,
  disabled,
  labelId,
  testId,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  labelId: string;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelId}
      data-testid={testId}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-control border px-0.5 outline-none transition-colors focus-visible:ring-1 focus-visible:ring-state/60 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked
          ? 'justify-end border-signal/50 bg-signal/80'
          : 'justify-start border-border bg-surface-2'
      }`}
    >
      <span
        className={`h-3.5 w-3.5 rounded-control transition-colors ${
          checked ? 'bg-signal-foreground' : 'bg-ink-2'
        }`}
      />
    </button>
  );
}

/** A small numeric field with its own label — used by the three auto-recharge inputs. */
function NumberField({
  id,
  label,
  hint,
  value,
  onChange,
  min,
  max,
  step,
  disabled,
  testId,
  placeholder,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (next: string) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  testId: string;
  placeholder?: string;
}) {
  return (
    <div className="min-w-0 flex-1">
      <label htmlFor={id} className="block text-xs font-medium text-foreground">
        {label}
      </label>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={step}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1 h-9 w-full rounded-control border border-border bg-surface-1 px-2.5 text-sm text-foreground outline-none transition-colors focus-visible:border-state/50 focus-visible:ring-1 focus-visible:ring-state/40 disabled:cursor-not-allowed disabled:opacity-50"
      />
      {hint ? <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export interface SettingsBillingSectionProps {
  /**
   * Ouvre la modale de recharge. Fournie par le shell, qui possède l'état de
   * `TopUpDialog` ; sans elle, le bouton retombe sur `?topup=1`, le chemin que
   * les surfaces hors shell empruntent déjà (voir `OsShell`).
   */
  onTopUp?: () => void;
}

export function SettingsBillingSection({ onTopUp }: SettingsBillingSectionProps = {}) {
  const { t, i18n } = useTranslation('dashboard');
  const locale = i18n.language;

  /**
   * LE SOLDE AGENT, et non celui du store.
   *
   * La carte lisait `GET /billing/credits` — `AccountProduct.credits`, les
   * copilotes kol/medwatch. Deux choses juste en dessous agissent maintenant sur
   * l'AUTRE grand-livre : « Acheter des crédits » ouvre la modale qui vend les
   * packs agent, et le rechargement automatique crédite `User.agentCredits`.
   * Laisser le compteur du store en tête aurait donné un panneau où l'on achète
   * des crédits et où le chiffre affiché ne bouge pas — le pire des deux
   * mondes. C'est aussi le solde que la jauge du rail montre, et le seul sur
   * lequel un tour d'agent est gagé.
   */
  const credits = useCreditsQuery();
  const paymentMethod = usePaymentMethod();
  const invoices = useInvoices();
  const setupCard = useSetupPaymentMethod();
  const setDefaultCard = useSetDefaultPaymentMethod();
  const removeCard = useRemovePaymentMethod();
  const autoRecharge = useAutoRecharge();
  const saveAutoRecharge = useUpdateAutoRecharge();

  const settings = autoRecharge.data;

  /* Le formulaire est un brouillon local : on ne veut PAS écrire à chaque frappe
     — chaque PUT est un réglage de prélèvement. Il se resynchronise quand le
     serveur répond, et `enabled` est le seul champ qui parte tout seul. */
  const [threshold, setThreshold] = useState('');
  const [amount, setAmount] = useState('');
  const [cap, setCap] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!settings) return;
    setThreshold(settings.threshold == null ? '' : String(settings.threshold));
    setAmount(settings.amount == null ? '' : String(settings.amount));
    setCap(settings.monthlyCapCents == null ? '' : String(Math.round(settings.monthlyCapCents / 100)));
  }, [settings]);

  const addCard = async () => {
    try {
      const data = await setupCard.mutateAsync();
      if (data?.url) window.location.href = data.url;
    } catch {
      // Surfaced by the API interceptor.
    }
  };

  const buyCredits = () => {
    if (onTopUp) {
      onTopUp();
      return;
    }
    // Hors du shell : le paramètre est consommé par `OsShell`, qui ouvre la modale.
    window.location.assign('/agent?topup=1');
  };

  const formatDate = (value?: string | null): string | null => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const invoiceStatusLabel = (status: Invoice['status']): string => {
    const key: Record<Invoice['status'], string> = {
      paid: 'settings.billing.invoices.statusPaid',
      open: 'settings.billing.invoices.statusOpen',
      void: 'settings.billing.invoices.statusVoid',
      uncollectible: 'settings.billing.invoices.statusUncollectible',
    };
    return t(key[status] ?? 'settings.billing.invoices.statusOpen');
  };

  const creditsData = credits.data;
  const cards: SavedCard[] = paymentMethod.data?.paymentMethods ?? [];
  const invoiceList = (invoices.data as Invoice[] | undefined) ?? [];

  /**
   * LE SERVEUR PEUT ÊTRE EN RETARD SUR CE FICHIER, et c'est arrivé : le front
   * est déployé par Amplify sur chaque push, le back par un job qui tourne
   * derrière les E2E. Pendant cette fenêtre, `GET /billing/auto-recharge`
   * répond l'ANCIENNE forme — sans `limits`, sans `currency`, sans
   * `spentThisMonthCents`. Lire `settings.limits.creditsPerEur` y jetait un
   * TypeError, donc un panneau blanc sur une surface de paiement.
   *
   * Les bornes restent celles du serveur quand il les donne ; ces valeurs ne
   * sont qu'un filet, et elles recopient les constantes de `pricing.config.ts`.
   * La route refuse de toute façon ce qu'elle refuse : le filet évite un écran
   * blanc, il n'autorise rien.
   */
  const limits = settings?.limits;
  const spentThisMonthCents = settings?.spentThisMonthCents ?? 0;
  const currency = (settings?.currency ?? 'eur').toUpperCase();
  const money = (minor: number) => formatCurrency({ minor }, currency, { locale });

  /** What the operator's typed amount would actually cost. Derived, never typed. */
  const creditsPerEur = limits?.creditsPerEur ?? 10;
  const typedAmount = Number.parseInt(amount, 10);
  const typedPriceCents =
    Number.isFinite(typedAmount) && typedAmount > 0
      ? Math.round((typedAmount * 100) / creditsPerEur)
      : null;

  const stripeReady = Boolean(settings?.configured);
  const hasCard = Boolean(settings?.hasPaymentMethod) || cards.length > 0;
  const canArm = stripeReady && hasCard;

  /**
   * Envoie le formulaire.
   *
   * LES TROIS CHAMPS PARTENT TOUJOURS, armé ou non — c'est ce que fait le
   * serveur, qui persiste seuil, montant et plafond quel que soit `enabled` et
   * ne valide qu'à l'activation. Ne les envoyer qu'armé rendrait « Enregistrer »
   * silencieusement inopérant sur un réglage éteint : l'opérateur reviendrait
   * plus tard, l'activerait, et retomberait sur ses anciennes valeurs.
   *
   * LA VALIDATION PORTE SUR CE QUI EST ÉCRIT, pas sur l'état du commutateur. Un
   * champ VIDE est un brouillon légitime — on peut poser un seuil sans encore
   * savoir le montant — et il part simplement absent, ce que le serveur lit comme
   * « ne touche pas à cette valeur ». Un champ REMPLI et hors bornes, en
   * revanche, est une erreur qu'il faut dire : la version précédente le laissait
   * tomber en silence quand le réglage était éteint, si bien que « Enregistrer »
   * répondait « Enregistré » sans avoir rien enregistré du montant tapé.
   *
   * S'y ajoute, à l'ACTIVATION seulement, l'exigence que seuil et montant soient
   * là : on ne peut pas armer un prélèvement sans dire quand ni combien.
   */
  const submitAutoRecharge = async (enabled: boolean) => {
    setFormError(null);
    const body: {
      enabled: boolean;
      threshold?: number;
      amount?: number;
      monthlyCapCents?: number | null;
    } = { enabled };

    const th = Number.parseInt(threshold, 10);
    const am = Number.parseInt(amount, 10);
    // Champ vide ⇒ `null`, c'est-à-dire « retire le plafond » — et non
    // « laisse-le tel quel ». L'opérateur qui efface le champ veut le retirer.
    const capEuros = cap.trim() === '' ? null : Number.parseInt(cap, 10);
    const min = limits?.minCredits ?? DEFAULT_LIMITS.minCredits;
    const max = limits?.maxCredits ?? DEFAULT_LIMITS.maxCredits;

    const thresholdWritten = threshold.trim() !== '';
    const amountWritten = amount.trim() !== '';

    // Ce qui est écrit doit être valide, armé ou non.
    if ((thresholdWritten || enabled) && (!Number.isFinite(th) || th < 1)) {
      setFormError(t('settings.billing.autoRecharge.errorThreshold'));
      return;
    }
    if ((amountWritten || enabled) && (!Number.isFinite(am) || am < min || am > max)) {
      setFormError(t('settings.billing.autoRecharge.errorAmount', { min, max }));
      return;
    }
    if (capEuros !== null && (!Number.isFinite(capEuros) || capEuros < 1)) {
      setFormError(t('settings.billing.autoRecharge.errorCap'));
      return;
    }

    if (thresholdWritten) body.threshold = th;
    if (amountWritten) body.amount = am;
    body.monthlyCapCents = capEuros === null ? null : capEuros * 100;

    try {
      await saveAutoRecharge.mutateAsync(body);
    } catch {
      setFormError(t('settings.billing.autoRecharge.errorSave'));
    }
  };

  return (
    <div className="space-y-4" data-testid="settings-billing">
      {/* ── Crédits ──────────────────────────────────────────── */}
      <Card data-testid="settings-billing-credits">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <CardTitle className="text-base">{t('settings.billing.credits.title')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">
              {credits.isLoading ? (
                <span className="text-muted-foreground">…</span>
              ) : (
                t('settings.billing.credits.balance', { balance: creditsData?.balance ?? 0 })
              )}
            </p>
          </div>
          <Button size="sm" data-testid="settings-billing-buy-credits" onClick={buyCredits}>
            {t('settings.billing.credits.buy')}
          </Button>
        </CardContent>
      </Card>

      {/* ── Moyens de paiement ───────────────────────────────── */}
      <Card data-testid="settings-billing-payment">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <CardTitle className="text-base">{t('settings.billing.payment.title')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {paymentMethod.isLoading ? (
            <p className="text-sm text-muted-foreground">…</p>
          ) : cards.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('settings.billing.payment.none')}</p>
          ) : (
            <ul className="space-y-2" data-testid="settings-billing-card-list">
              {cards.map((card) => (
                <li
                  key={card.id}
                  data-testid="settings-billing-card"
                  className="flex flex-wrap items-center justify-between gap-2 rounded-control border border-border px-3 py-2"
                >
                  <div className="min-w-0">
                    {/* `div` et non `p` : `Badge` rend un `div`, et un bloc dans
                        un paragraphe est du HTML invalide — React le signale, et
                        le navigateur ferme le `p` avant le badge, ce qui casse
                        l'alignement de la ligne. */}
                    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                      {t('settings.billing.payment.card', {
                        brand: titleCase(card.brand),
                        last4: card.last4,
                      })}
                      {card.isDefault ? (
                        <Badge variant="outline" className="text-[0.65rem]">
                          {t('settings.billing.payment.default')}
                        </Badge>
                      ) : null}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('settings.billing.payment.expires', {
                        month: String(card.expMonth).padStart(2, '0'),
                        year: card.expYear,
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    {card.isDefault ? null : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        data-testid="settings-billing-card-default"
                        onClick={() => void setDefaultCard.mutateAsync(card.id).catch(() => {})}
                        disabled={setDefaultCard.isPending || removeCard.isPending}
                      >
                        {t('settings.billing.payment.makeDefault')}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-muted-foreground"
                      data-testid="settings-billing-card-remove"
                      aria-label={t('settings.billing.payment.remove')}
                      onClick={() => void removeCard.mutateAsync(card.id).catch(() => {})}
                      disabled={setDefaultCard.isPending || removeCard.isPending}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Retirer la DERNIÈRE carte désarme la recharge côté serveur. Le dire
              ici, une fois, plutôt que de laisser l'opérateur le découvrir en
              rouvrant le panneau. */}
          {paymentMethod.data?.autoRechargeDisabled ? (
            <p
              role="status"
              data-testid="settings-billing-autorecharge-disarmed"
              className="flex items-start gap-1.5 text-xs text-muted-foreground"
            >
              <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t('settings.billing.payment.autoRechargeDisarmed')}
            </p>
          ) : null}

          <Button
            variant="outline"
            size="sm"
            data-testid="settings-billing-payment-add"
            onClick={addCard}
            disabled={setupCard.isPending}
          >
            {setupCard.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <>
                <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                {t('settings.billing.payment.add')}
              </>
            )}
          </Button>
          <p className="text-[11px] leading-snug text-muted-foreground">
            {t('settings.billing.payment.hostedNote')}
          </p>
        </CardContent>
      </Card>

      {/* ── Rechargement automatique ─────────────────────────── */}
      <Card data-testid="settings-billing-autorecharge">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <CardTitle className="text-base" id="autorecharge-label">
                {t('settings.billing.autoRecharge.title')}
              </CardTitle>
            </div>
            <BillingToggle
              checked={Boolean(settings?.enabled)}
              onChange={(next) => void submitAutoRecharge(next)}
              disabled={!canArm || autoRecharge.isLoading || saveAutoRecharge.isPending}
              labelId="autorecharge-label"
              testId="settings-billing-autorecharge-toggle"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs leading-relaxed text-muted-foreground">
            {t('settings.billing.autoRecharge.description')}
          </p>

          {/* Deux empêchements distincts, deux phrases : l'un se règle en
              ajoutant une carte, l'autre ne se règle pas depuis ici. */}
          {!autoRecharge.isLoading && !stripeReady ? (
            <p
              className="text-xs text-muted-foreground"
              data-testid="settings-billing-autorecharge-unavailable"
            >
              {t('settings.billing.autoRecharge.unavailable')}
            </p>
          ) : null}
          {!autoRecharge.isLoading && stripeReady && !hasCard ? (
            <p
              className="text-xs text-muted-foreground"
              data-testid="settings-billing-autorecharge-nocard"
            >
              {t('settings.billing.autoRecharge.needsCard')}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <NumberField
              id="autorecharge-threshold"
              label={t('settings.billing.autoRecharge.thresholdLabel')}
              hint={t('settings.billing.autoRecharge.thresholdHint')}
              value={threshold}
              onChange={setThreshold}
              min={1}
              max={limits?.maxThreshold ?? DEFAULT_LIMITS.maxThreshold}
              disabled={!canArm}
              testId="settings-billing-autorecharge-threshold"
            />
            <NumberField
              id="autorecharge-amount"
              label={t('settings.billing.autoRecharge.amountLabel')}
              hint={
                typedPriceCents === null
                  ? t('settings.billing.autoRecharge.amountHint', {
                      min: limits?.minCredits ?? DEFAULT_LIMITS.minCredits,
                      max: limits?.maxCredits ?? DEFAULT_LIMITS.maxCredits,
                    })
                  : t('settings.billing.autoRecharge.amountPrice', { price: money(typedPriceCents) })
              }
              value={amount}
              onChange={setAmount}
              min={limits?.minCredits ?? DEFAULT_LIMITS.minCredits}
              max={limits?.maxCredits ?? DEFAULT_LIMITS.maxCredits}
              disabled={!canArm}
              testId="settings-billing-autorecharge-amount"
            />
            <NumberField
              id="autorecharge-cap"
              label={t('settings.billing.autoRecharge.capLabel')}
              hint={t('settings.billing.autoRecharge.capHint')}
              value={cap}
              onChange={setCap}
              min={1}
              max={Math.round(
                (limits?.maxMonthlyCapCents ?? DEFAULT_LIMITS.maxMonthlyCapCents) / 100,
              )}
              disabled={!canArm}
              testId="settings-billing-autorecharge-cap"
              placeholder={t('settings.billing.autoRecharge.capNone')}
            />
          </div>

          {/* La dépense du mois n'a d'intérêt qu'une fois le réglage armé ou une
              recharge passée. Un « 0,00 € » sous un formulaire éteint n'apprend
              rien et ajoute une ligne à lire. */}
          {settings && (settings.enabled || spentThisMonthCents > 0) ? (
            <p
              className="font-data text-[11px] uppercase tracking-[0.1em] text-muted-foreground"
              data-testid="settings-billing-autorecharge-spent"
            >
              {t('settings.billing.autoRecharge.spent', {
                spent: money(spentThisMonthCents),
              })}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              data-testid="settings-billing-autorecharge-save"
              onClick={() => void submitAutoRecharge(Boolean(settings?.enabled))}
              disabled={!canArm || saveAutoRecharge.isPending}
            >
              {saveAutoRecharge.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                t('settings.billing.autoRecharge.save')
              )}
            </Button>
            {saveAutoRecharge.isSuccess && !formError ? (
              <span
                role="status"
                data-testid="settings-billing-autorecharge-saved"
                className="flex items-center gap-1 text-xs text-muted-foreground"
              >
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                {t('settings.billing.autoRecharge.saved')}
              </span>
            ) : null}
          </div>

          {formError ? (
            <p
              role="alert"
              data-testid="settings-billing-autorecharge-error"
              className="flex items-center gap-1.5 text-xs text-destructive"
            >
              <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {formError}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {/* ── Factures ─────────────────────────────────────────── */}
      <Card data-testid="settings-billing-invoices">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <CardTitle className="text-base">{t('settings.billing.invoices.title')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {invoices.isLoading ? (
            <p className="text-sm text-muted-foreground">…</p>
          ) : invoiceList.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('settings.billing.invoices.empty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full text-left text-sm"
                data-testid="settings-billing-invoices-table"
              >
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">{t('settings.billing.invoices.date')}</th>
                    <th className="py-2 pr-3 font-medium">{t('settings.billing.invoices.total')}</th>
                    <th className="py-2 pr-3 font-medium">
                      {t('settings.billing.invoices.status')}
                    </th>
                    <th className="py-2 text-right font-medium">
                      {t('settings.billing.invoices.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceList.map((inv) => (
                    <tr
                      key={inv.id}
                      className="border-b border-border/60 last:border-0"
                      data-testid="settings-billing-invoice-row"
                    >
                      <td className="py-2 pr-3 text-foreground">{formatDate(inv.date) ?? '—'}</td>
                      <td className="py-2 pr-3 text-foreground">
                        {formatCurrency({ minor: inv.amount }, inv.currency, { locale })}
                      </td>
                      <td className="py-2 pr-3">
                        <Badge variant="outline" className="text-[0.7rem]">
                          {invoiceStatusLabel(inv.status)}
                        </Badge>
                      </td>
                      <td className="py-2 text-right">
                        {/* Deux liens et non un : « Voir » ouvre le reçu hébergé
                            (celui qu'on relit), « PDF » télécharge la pièce
                            comptable. Les fournir sous un seul bouton obligeait à
                            passer par la page hébergée pour obtenir le fichier. */}
                        <div className="flex items-center justify-end gap-1">
                          {inv.hostedUrl ? (
                            <a href={inv.hostedUrl} target="_blank" rel="noopener noreferrer">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                data-testid="settings-billing-invoice-view"
                              >
                                {t('settings.billing.invoices.view')}
                                <ExternalLink className="ml-1 h-3 w-3" aria-hidden="true" />
                              </Button>
                            </a>
                          ) : null}
                          {inv.pdfUrl ? (
                            <a href={inv.pdfUrl} target="_blank" rel="noopener noreferrer">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                data-testid="settings-billing-invoice-pdf"
                              >
                                {t('settings.billing.invoices.pdf')}
                                <Download className="ml-1 h-3 w-3" aria-hidden="true" />
                              </Button>
                            </a>
                          ) : null}
                          {!inv.hostedUrl && !inv.pdfUrl ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
